import React, { createContext, useContext, useState, useCallback } from 'react';

type BreadcrumbContextValue = {
  breadcrumbLabel: string | null;
  setBreadcrumbLabel: (label: string | null) => void;
  breadcrumbSuffix: string | null;
  setBreadcrumbSuffix: (label: string | null) => void;
  breadcrumbActions: React.ReactNode | null;
  setBreadcrumbActions: (content: React.ReactNode | null) => void;
  /** True when header should use short search + icon-only actions. */
  headerCompact: boolean;
  setHeaderCompact: (compact: boolean) => void;
  /** True when breadcrumbs, search, and actions fit in one row. */
  headerInline: boolean;
  setHeaderInline: (inline: boolean) => void;
};

export const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const [breadcrumbLabel, setBreadcrumbLabelState] = useState<string | null>(null);
  const [breadcrumbSuffix, setBreadcrumbSuffixState] = useState<string | null>(null);
  const [breadcrumbActions, setBreadcrumbActionsState] = useState<React.ReactNode | null>(null);
  const [headerCompact, setHeaderCompactState] = useState(true);
  const [headerInline, setHeaderInlineState] = useState(false);

  const setBreadcrumbLabel = useCallback((label: string | null) => {
    setBreadcrumbLabelState(() => label);
  }, []);
  const setBreadcrumbSuffix = useCallback((label: string | null) => {
    setBreadcrumbSuffixState(() => label);
  }, []);
  const setBreadcrumbActions = useCallback((content: React.ReactNode | null) => {
    setBreadcrumbActionsState(() => content);
  }, []);
  const setHeaderCompact = useCallback((compact: boolean) => {
    setHeaderCompactState(() => compact);
  }, []);
  const setHeaderInline = useCallback((inline: boolean) => {
    setHeaderInlineState(() => inline);
  }, []);

  return (
    <BreadcrumbContext.Provider
      value={{
        breadcrumbLabel,
        setBreadcrumbLabel,
        breadcrumbSuffix,
        setBreadcrumbSuffix,
        breadcrumbActions,
        setBreadcrumbActions,
        headerCompact,
        setHeaderCompact,
        headerInline,
        setHeaderInline,
      }}
    >
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbLabel() {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    throw new Error('useBreadcrumbLabel must be used within BreadcrumbProvider');
  }
  return ctx;
}

export function useContentHeaderLayout() {
  const ctx = useContext(BreadcrumbContext);
  return {
    compact: ctx?.headerCompact ?? true,
    inline: ctx?.headerInline ?? false,
  };
}
